import React from 'react';
import { Product } from '../data/mockData';

interface ProductsSectionProps {
  products: Product[];
  currentProduct: Product | undefined;
  onProductSelect: (product: Product) => void;
}

const ProductsSection: React.FC<ProductsSectionProps> = ({
  products,
  currentProduct,
  onProductSelect
}) => {
  const categories = [
    {
      id: 'tshirts',
      name: 'T-Shirts',
      description: 'Custom t-shirts',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-600',
      hoverColor: 'hover:bg-blue-100'
    },
    {
      id: 'mugs',
      name: 'Mugs',
      description: 'Custom mugs',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200',
      textColor: 'text-green-600',
      hoverColor: 'hover:bg-green-100'
    },
    {
      id: 'posters',
      name: 'Posters',
      description: 'Custom posters',
      bgColor: 'bg-purple-50',
      borderColor: 'border-purple-200',
      textColor: 'text-purple-600',
      hoverColor: 'hover:bg-purple-100'
    },
    {
      id: 'stickers',
      name: 'Stickers',
      description: 'Custom stickers',
      bgColor: 'bg-orange-50',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-600',
      hoverColor: 'hover:bg-orange-100'
    }
  ];

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4 text-gray-800">Products</h3>
      
      <div className="space-y-6">
        {/* Product Categories */}
        <div>
          <h4 className="text-md font-medium mb-3 text-gray-700">Product Categories</h4>
          <div className="grid grid-cols-2 gap-3">
            {categories.map((category) => (
              <div 
                key={category.id}
                className={`${category.bgColor} border ${category.borderColor} rounded-lg p-3 cursor-pointer ${category.hoverColor} transition-colors`}
              >
                <div className={`${category.textColor} font-medium`}>{category.name}</div>
                <div className={`text-sm ${category.textColor.replace('600', '500')}`}>{category.description}</div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Product List */}
        <div>
          <h4 className="text-md font-medium mb-3 text-gray-700">All Products</h4>
          <div className="space-y-3">
            {products.map((productItem: Product) => (
              <div 
                key={productItem.id}
                className={`bg-white border rounded-lg p-3 cursor-pointer hover:shadow-md transition-all ${
                  productItem.id === (currentProduct?.id || '') 
                    ? 'border-blue-500 bg-blue-50' 
                    : 'border-gray-300 hover:border-blue-500'
                }`}
                onClick={() => onProductSelect(productItem)}
              >
                <div className="flex items-center space-x-3">
                  {/* Product Image */}
                  <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                    {productItem.image ? (
                      <img 
                        src={productItem.image} 
                        alt={productItem.name}
                        className="w-full h-full object-cover rounded-lg"
                      />
                    ) : (
                      <div className="text-gray-500 text-xs">No Image</div>
                    )}
                  </div>
                  
                  {/* Product Info */}
                  <div className="flex-1">
                    <div className="font-medium text-gray-800 mb-1">{productItem.name}</div>
                    <div className="text-sm text-gray-500 mb-1">{productItem.description}</div>
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-blue-600">${productItem.price.toFixed(2)}</div>
                      <div className="text-xs text-gray-400 capitalize">{productItem.category}</div>
                    </div>
                  </div>
                </div>
                
                {/* Product Details */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div>
                      <span className="font-medium">Colors:</span> {productItem.colors.length}
                    </div>
                    <div>
                      <span className="font-medium">Sizes:</span> {productItem.sizes.length}
                    </div>
                    <div>
                      <span className="font-medium">Templates:</span> {productItem.templates.length}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsSection; 